import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getOfflineQueueCount } from '../services/api';
import '../styles/OfflineIndicator.css';

const OfflineIndicator = () => {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(true); // Start as online by default
  const [showIndicator, setShowIndicator] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const updatePendingCount = async () => {
    const count = await getOfflineQueueCount();
    setPendingCount(count);
  };

  useEffect(() => {
    updatePendingCount();
    const interval = setInterval(updatePendingCount, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Delay initial check to avoid false offline detection on reload
    const initialCheckTimer = setTimeout(() => {
      setIsOnline(navigator.onLine);
      // Only show indicator if actually offline
      if (!navigator.onLine) {
        setShowIndicator(true);
      }
    }, 500); // Wait 500ms before first check

    const handleOnline = () => {
      setIsOnline(true);
      // Show indicator for 4 seconds when coming back online
      setShowIndicator(true);
      setTimeout(() => {
        setShowIndicator(false);
        setPendingCount(0);
      }, 4000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowIndicator(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearTimeout(initialCheckTimer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showIndicator) return null;

  return (
    <div className={`offline-indicator ${isOnline ? 'online' : 'offline'}`}>
      <div className="offline-indicator-content">
        <span className="offline-indicator-icon">
          {isOnline ? '✓' : '⚠'}
        </span>
        <span className="offline-indicator-text">
          {isOnline 
            ? (pendingCount > 0 
              ? `${t('connectionRestored')} (${pendingCount} ${t('pendingChanges')})` 
              : t('connectionRestored'))
            : `${t('offlineWarning')} ${pendingCount > 0 ? `(${pendingCount} ${t('pendingChanges')})` : ''}`}
        </span>
      </div>
    </div>
  );
};

export default OfflineIndicator;
